import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Stepper,
  Step,
  StepLabel,
  Card,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { PayappProcessProvider, usePayappProcess, FirstPayappSnapshot, SecondPayappUploadResult } from '../context/PayappProcessContext';
import PayappProcessFooter from '../components/payapp-process/PayappProcessFooter';
import CoverPageVerification from '../components/payapp-process/CoverPageVerification';
import {
  labelToBackendFieldsMap,
  getValueFromBackendFields,
  postProcessPeriodCoverValues,
  finalFieldsHeaders,
  stepTooltips,
  stepLabels,
  RAW_VALUE_LABELS,
} from '../components/file-upload/Payapp.util';
import TableVerification from '../components/payapp-process/TableVerification';
import PreviewAndConfirm, { PreviewAndConfirmRef } from '../components/payapp-process/PreviewAndConfirm';
import theme from 'assets/theme';
import { LineItemWithDivision } from '../components/glossary/glossary.type';
import { useAppContext } from '../common/AppContext';
import { formatCurrency, differsWithBuffer } from '../common/utils/common.util';
import PayAppUploadDialog from '../components/file-upload/PayappUploadDialog';
import { PayappUploadResult } from '../components/file-upload/PayappUploadDialog';
import { normalizeDescription } from '../components/file-upload/Payapp.util';
import { ROUTES } from 'common/constants/urls';
import { getUserColumnPreferences, saveUserColumnPreferences } from '../components/project/project.api';
import { PAGE_KEYS } from '../components/report/constants';

export interface PayappProcessStep {
  label: string;
  tooltip: string;
}
export const payappProcessSteps: PayappProcessStep[] = [
  { label: stepLabels.VerifyCoverPage, tooltip: stepTooltips.VerifyCoverPage },
  { label: stepLabels.VerifyTable, tooltip: stepTooltips.VerifyTable },
  { label: stepLabels.MergeLineItems, tooltip: stepTooltips.MergeLineItems },
  { label: stepLabels.CompareAgainstLastMonth, tooltip: stepTooltips.CompareAgainstLastMonth },
];

export const payappStepIds = ['verify-cover-page', 'verify-table', 'merge-line-items', 'compare-against-last-month'] as const;

const PAYAPP_MERGE_STEP_INDEX = 2; // Step 3 "Merge Into Last Month" (0-based)

const PayappProcessContent: React.FC = () => {
  const { state, resetForSecondPayapp, setActiveStep } = usePayappProcess();
  const { setPageHeaderText, setBreadcrumbProjectLabel } = useAppContext();
  const previewAndConfirmRef = useRef<PreviewAndConfirmRef>(null);
  const [pdfAccordionExpanded, setPdfAccordionExpanded] = React.useState(false);
  const [budgetSummaryExpanded, setBudgetSummaryExpanded] = React.useState(true);
  // Tracks the user's explicit PDF preference so handlers don't close over stale state
  const pdfUserPreferenceRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    projectId: routeProjectId,
    payappDrawId: routePayappDrawId,
    stepId,
  } = useParams<{
    projectId: string;
    payappDrawId: string;
    stepId?: string;
  }>();

  // Second-payapp upload dialog state
  const [showSecondPayappDialog, setShowSecondPayappDialog] = useState(false);
  const [secondPayappDialogTitle, setSecondPayappDialogTitle] = useState<React.ReactNode>('Upload Second PayApp');
  const [secondPayappDialogActions, setSecondPayappDialogActions] = useState<React.ReactNode>(null);

  /** Builds a snapshot of payapp-1 line-item values from the current merged projectDraw */
  const buildPayapp1Snapshot = (): FirstPayappSnapshot => {
    const lineItemValuesById: FirstPayappSnapshot['lineItemValuesById'] = {};

    const items = state.projectDraw?.lineItems?.filter((item: any) => !item.isDeleted) || [];
    items.forEach((item: any) => {
      // Use the project line-item id as primary key; fall back to a description-based key
      // for payapp-only rows that do not have a stable project id.
      const key = item.isCreated ? `desc:${normalizeDescription(item.lineItem?.description || '')}` : item.id;

      lineItemValuesById[key] = {
        originalBudget: item.originalBudget ?? undefined,
        reallocation: item.reallocation ?? undefined,
        changeOrder: item.changeOrder ?? undefined,
        revisedBudget: item.revisedBudget ?? undefined,
        previousApplication: item.previousApplication ?? undefined,
        completeThisPeriod: item.completeThisPeriod ?? undefined,
        storedThisPeriod: item.storedThisPeriod ?? undefined,
        completedStoredToDate: item.completedStoredToDate ?? undefined,
        balanceToComplete: item.balanceToComplete ?? undefined,
        retainage: item.retainage ?? undefined,
      };
    });

    return {
      coverPageValues: state.coverPageValues,
      lineItemValuesById,
    };
  };

  /**
   * Called when the user has successfully uploaded payapp-2.
   * Captures payapp-1 snapshot then resets the workflow for payapp-2.
   */
  const handleSecondPayappUploaded = (result: PayappUploadResult) => {
    const snapshot = buildPayapp1Snapshot();

    // Derive initial field headers mapping for payapp-2 from the upload result
    const initialFields = Object.keys(result.parsedTable?.[0] || {});
    const fieldHeadersMapping = initialFields.reduce((acc: Record<string, string>, key: string) => {
      const header = finalFieldsHeaders.find(option => option.field === key)?.headerName;
      acc[key] = header ?? key;
      return acc;
    }, {});

    // Compute cover page values for payapp-2 using the same mapping logic as the first workflow
    const coverPageValuesForSecond: Record<string, string> = postProcessPeriodCoverValues(
      Object.fromEntries(
        Object.entries(labelToBackendFieldsMap).map(([label, backendFields]) => [
          label,
          getValueFromBackendFields(result.coverFields || {}, backendFields, RAW_VALUE_LABELS.has(label)),
        ]),
      ),
    );

    const payapp2Data: SecondPayappUploadResult = {
      parsedTable: result.parsedTable,
      coverFields: result.coverFields,
      sourceTargetHeadersMapping: result.sourceTargetHeadersMapping,
      fieldHeadersMapping,
      fileUrl: result.fileUrl,
      coverPageValues: coverPageValuesForSecond,
    };

    resetForSecondPayapp(snapshot, payapp2Data);
    // Ensure URL reflects the first step of the second-payapp workflow
    updateUrlForStep(0, true);
    setShowSecondPayappDialog(false);
  };

  // Load accordion preferences once on mount
  React.useEffect(() => {
    getUserColumnPreferences(PAGE_KEYS.PAYAPP_PROCESS)
      .then(prefs => {
        if (prefs?.columnsJson) {
          try {
            const parsed = JSON.parse(prefs.columnsJson);
            if (typeof parsed.pdfExpanded === 'boolean') {
              pdfUserPreferenceRef.current = parsed.pdfExpanded;
              // Only apply immediately if not on the merge step — the activeStep effect owns that state there
              if (state.activeStep !== PAYAPP_MERGE_STEP_INDEX) {
                setPdfAccordionExpanded(parsed.pdfExpanded);
              }
            }
          } catch {
            // ignore malformed JSON
          }
        }
      })
      .catch(_err => undefined);
  }, []);

  // On merge step: collapse PDF; on all other steps: restore to user's preference
  React.useEffect(() => {
    if (state.activeStep === PAYAPP_MERGE_STEP_INDEX) {
      setPdfAccordionExpanded(false);
    } else {
      setPdfAccordionExpanded(pdfUserPreferenceRef.current);
    }
  }, [state.activeStep]);

  const handlePdfAccordionChange = (_: React.SyntheticEvent, expanded: boolean) => {
    setPdfAccordionExpanded(expanded);
    pdfUserPreferenceRef.current = expanded;
    saveUserColumnPreferences(PAGE_KEYS.PAYAPP_PROCESS, {
      pdfExpanded: expanded,
    });
  };

  const handleBudgetSummaryChange = (expanded: boolean) => {
    setBudgetSummaryExpanded(expanded);
  };

  useEffect(() => {
    // Set custom page header with project information
    if (state.projectNumber && state.projectName) {
      setPageHeaderText(`Process payapp / ${state.projectNumber} - ${state.projectName}`);
      setBreadcrumbProjectLabel(`${state.projectNumber} ${state.projectName}`);
    } else {
      setPageHeaderText('Process payapp');
      setBreadcrumbProjectLabel(undefined);
    }

    // Cleanup function to reset page header and breadcrumb when component unmounts
    return () => {
      setPageHeaderText('');
      setBreadcrumbProjectLabel(undefined);
    };
  }, [state.projectNumber, state.projectName, setPageHeaderText, setBreadcrumbProjectLabel]);

  const updateUrlForStep = React.useCallback(
    (stepIndex: number, replace = false) => {
      const projectId = routeProjectId || state.projectId;
      const payappDrawId = routePayappDrawId || state.payappDrawId;
      if (!projectId || !payappDrawId) return;
      const stepKey = payappStepIds[stepIndex] || payappStepIds[0];
      const path = `${ROUTES.PROJECTS}/${projectId}${ROUTES.PROCESSPAYAPP}/${payappDrawId}/${stepKey}`;
      navigate(path, { replace, state: location.state });
    },
    [navigate, location.state, routeProjectId, routePayappDrawId, state.projectId, state.payappDrawId],
  );

  // Keep a ref of the latest activeStep so the URL→state effect doesn't depend
  // on it (which would cause step bounces when local step changes race with URL updates).
  const activeStepRef = useRef(state.activeStep);
  activeStepRef.current = state.activeStep;

  // Sync URL -> state.activeStep (e.g. on direct load or browser back/forward)
  useEffect(() => {
    if (!stepId) {
      // Default to first step if none specified
      updateUrlForStep(activeStepRef.current || 0, true);
      return;
    }
    const indexFromUrl = payappStepIds.indexOf(stepId as (typeof payappStepIds)[number]);
    if (indexFromUrl >= 0 && indexFromUrl !== activeStepRef.current) {
      setActiveStep(indexFromUrl);
    }
  }, [stepId, setActiveStep, updateUrlForStep]);

  // Do NOT sync state.activeStep -> URL in an effect: it causes a loop because
  // navigate() changes location, which changes updateUrlForStep ref, which re-runs the effect.
  // URL is updated only when user clicks Next/Back via the callback passed to the footer.

  useEffect(() => {
    return () => {
      if (state.fileUrl) {
        URL.revokeObjectURL(state.fileUrl);
      }
    };
  }, [state.fileUrl]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <CoverPageVerification />;
      case 1:
        return <TableVerification />;
      case 2:
      case 3:
        return (
          <PreviewAndConfirm
            ref={previewAndConfirmRef}
            budgetSummaryExpanded={budgetSummaryExpanded}
            onBudgetSummaryChange={handleBudgetSummaryChange}
          />
        );
      default:
        return null;
    }
  };

  // Step 3 (Merge) warning banner: reallocation mismatch and unmerged line items
  const lineItems = state.projectDraw?.lineItems?.filter((item: any) => !item.isDeleted) || [];

  const hasReallocationDiscrepancy = (item: any): boolean => {
    if (!item.payappRow && !item.projectRow) return false;
    const sys = Number(item.reallocation ?? 0);
    const payapp = item.payappRow?.reallocation;
    const project = item.projectRow?.reallocation;

    if (payapp !== undefined && differsWithBuffer(sys, payapp)) return true;
    if (project !== undefined && differsWithBuffer(sys, project)) return true;

    return false;
  };

  // Only consider reallocations on rows whose Reallocation cell is currently a discrepancy (red box)
  const discrepantReallocationItems = lineItems.filter((item: any) => hasReallocationDiscrepancy(item));
  const unexpectedReallocationTotal = discrepantReallocationItems.reduce(
    (sum: number, item: any) => sum + (item.reallocation || 0) + (item.changeOrder || 0),
    0,
  );
  const reallocationDifference = Math.abs(unexpectedReallocationTotal);
  const hasReallocationMismatch = discrepantReallocationItems.length > 0;

  const payappOnlyCount = lineItems.filter((item: any) => item.isCreated).length;
  const projectOnlyCount = lineItems.filter((item: any) => item.exitingAndNotMatched).length;
  const hasUnmatchedLineItems = payappOnlyCount > 0 || projectOnlyCount > 0;
  const allLineItemsArePayappOnly = lineItems.length > 0 && payappOnlyCount === lineItems.length;

  const unmatchedLineItemsDescription = (() => {
    if (!hasUnmatchedLineItems) return '';

    const parts: string[] = [];

    if (payappOnlyCount > 0) {
      parts.push(`${payappOnlyCount} payapp row${payappOnlyCount !== 1 ? 's' : ''}`);
    }

    if (projectOnlyCount > 0) {
      parts.push(`${projectOnlyCount} row${projectOnlyCount !== 1 ? 's' : ''} from last month`);
    }

    if (!parts.length) return '';

    return `There are unmerged line items (${parts.join(' and ')}). Merge below to resolve discrepancy.`;
  })();

  const showMergeStepBanner =
    state.activeStep === PAYAPP_MERGE_STEP_INDEX && (hasReallocationMismatch || (!allLineItemsArePayappOnly && hasUnmatchedLineItems));

  return (
    <Stack sx={styles.container}>
      <Card sx={styles.card}>
        {!state.isManualUpdate && (
          <Stepper activeStep={state.activeStep} alternativeLabel sx={styles.stepper}>
            {payappProcessSteps.map(step => (
              <Step key={step.label}>
                <Tooltip title={step.tooltip} placement='bottom' arrow>
                  <StepLabel>{step.label}</StepLabel>
                </Tooltip>
              </Step>
            ))}
          </Stepper>
        )}

        {!state.isManualUpdate && (
          <Stack sx={styles.pdfSection}>
            {showMergeStepBanner && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                <Stack spacing={0.5}>
                  {hasReallocationMismatch && (
                    <Typography variant='body2'>
                      {reallocationDifference === 0
                        ? 'The system has identified unexpected reallocations this month.'
                        : `The system has identified ${formatCurrency(
                            reallocationDifference.toString(),
                          )} in unexpected reallocations this month.`}
                    </Typography>
                  )}
                  {!allLineItemsArePayappOnly && hasUnmatchedLineItems && unmatchedLineItemsDescription && (
                    <Typography variant='body2'>{unmatchedLineItemsDescription}</Typography>
                  )}
                </Stack>
              </Alert>
            )}
            <Accordion expanded={pdfAccordionExpanded} onChange={handlePdfAccordionChange}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={styles.accordionSummary}>
                <Typography variant='subtitle1'>PDF</Typography>
              </AccordionSummary>
              <AccordionDetails sx={styles.accordionDetails}>
                <Card sx={styles.pdfCard}>
                  {state.fileUrl ? (
                    <iframe src={state.fileUrl} style={styles.pdfIframe} title='PayApp PDF Viewer' />
                  ) : (
                    <Stack sx={styles.pdfErrorContainer}>
                      <Typography>Could not load PDF file!</Typography>
                    </Stack>
                  )}
                </Card>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}

        <Stack sx={styles.contentSection}>{renderStepContent(state.activeStep)}</Stack>
      </Card>

      <PayappProcessFooter
        getNoteContent={() => {
          if (previewAndConfirmRef.current) {
            return previewAndConfirmRef.current.getNoteContent();
          }
          return state.notes[0]?.content || '';
        }}
        onUploadSecondPayapp={() => setShowSecondPayappDialog(true)}
        onStepUrlChange={updateUrlForStep}
      />

      {/* Second payapp upload dialog */}
      <Dialog open={showSecondPayappDialog} onClose={() => setShowSecondPayappDialog(false)} fullWidth maxWidth='md'>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='h5'>{secondPayappDialogTitle}</Typography>
          <IconButton onClick={() => setShowSecondPayappDialog(false)} sx={{ marginLeft: 'auto' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PayAppUploadDialog
            filesToAccept={{ 'application/pdf': ['.pdf'] }}
            projectId={state.projectId}
            projectNumber={state.projectNumber}
            projectName={state.projectName}
            payappDrawId={state.payappDrawId}
            projectDraw={state.originalProjectDraw}
            divisions={state.divisions}
            context='project'
            onClose={() => setShowSecondPayappDialog(false)}
            setDialogTitle={setSecondPayappDialogTitle}
            setDialogActions={setSecondPayappDialogActions}
            onUploadComplete={handleSecondPayappUploaded}
          />
        </DialogContent>
        {secondPayappDialogActions && <DialogActions>{secondPayappDialogActions}</DialogActions>}
      </Dialog>
    </Stack>
  );
};

const PayappProcess: React.FC = () => {
  const location = useLocation();
  const {
    parsedTable,
    coverFields,
    projectId,
    payappDrawId,
    projectDraw,
    divisions,
    fileUrl,
    projectNumber,
    projectName,
    sourceTargetHeadersMapping,
    isManualUpdate,
    // Draft-specific data
    isDraft,
    draftActiveStep,
    draftFieldHeadersMapping,
    draftSelectedRowIds,
    draftNotes,
    // Second-payapp flow (passed via location.state when restoring from draft)
    isSecondPayappFlow: locationIsSecondPayappFlow,
    firstPayappSnapshot: locationFirstPayappSnapshot,
  } = location.state || {};

  // Initialize cover page values with the new flexible mapping system
  const initialCoverPageValues: Record<string, string> =
    isDraft && coverFields
      ? coverFields // Use the converted display labels directly for drafts
      : postProcessPeriodCoverValues(
          Object.fromEntries(
            Object.entries(labelToBackendFieldsMap).map(([label, backendFields]) => [
              label,
              getValueFromBackendFields(coverFields || {}, backendFields, RAW_VALUE_LABELS.has(label)),
            ]),
          ),
        );

  // Initialize field headers mapping - use draft data if available
  const initialFields = Object.keys(parsedTable?.[0] || {});
  const initialFieldHeadersMapping =
    isDraft && draftFieldHeadersMapping
      ? draftFieldHeadersMapping
      : initialFields.reduce((acc: Record<string, string>, key: string) => {
          const header = finalFieldsHeaders.find(option => option.field === key)?.headerName;
          acc[key] = header ?? key;
          return acc;
        }, {});

  const initialState = {
    activeStep: isManualUpdate ? 3 : isDraft ? draftActiveStep || 0 : 0,
    isManualUpdate: isManualUpdate || false,
    parsedTable: parsedTable || [],
    coverFields: coverFields || {},
    coverPageValues: initialCoverPageValues,
    projectId: projectId || '',
    projectNumber: projectNumber || '',
    projectName: projectName || '',
    payappDrawId: payappDrawId || '',
    fieldHeadersMapping: initialFieldHeadersMapping,
    sourceTargetHeadersMapping: sourceTargetHeadersMapping || {},
    tableRows: parsedTable || [],
    selectedRowIds: isDraft ? draftSelectedRowIds || [] : [],
    originalProjectDraw: projectDraw || null,
    projectDraw: projectDraw || null,
    divisions: divisions || [],
    finalizedParsedRows: [],
    headersFixed: false,
    fixingHeaders: false,
    fileUrl: fileUrl || null,
    lineItemDivisions: [] as LineItemWithDivision[],
    ocrIssues: {},
    ocrFixedFields: new Set<string>(),
    ocrIssuesDetected: false,
    notes: isDraft ? draftNotes || [] : [],
    isSecondPayappFlow: locationIsSecondPayappFlow || false,
    firstPayappSnapshot: locationFirstPayappSnapshot || null,
    secondPayappTableRows: null,
  };

  return (
    <PayappProcessProvider initialState={initialState}>
      <PayappProcessContent />
    </PayappProcessProvider>
  );
};

const styles = {
  container: {
    minHeight: '120vh',
  },
  card: {
    p: 2,
    flex: 1,
  },
  stepper: {
    position: 'sticky',
    top: 85,
    paddingY: 2,
    zIndex: 1000,
    backgroundColor: 'background.paper',
    my: 4,
    '& .MuiStepLabel-label': {
      fontSize: '0.75rem',
    },
    '& .MuiStepLabel-label.Mui-active': {
      fontSize: '0.9rem',
      fontWeight: 'bold',
    },
    '& .MuiStepLabel-iconContainer.Mui-active': {
      fontSize: 'larger',
    },
    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line, & .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
      borderColor: theme.palette.primary.main,
    },
  },
  pdfSection: {
    mb: 2,
  },
  accordionSummary: {
    backgroundColor: 'background.paper',
    '& .MuiAccordionSummary-content': {
      my: 1,
    },
  },
  accordionDetails: {
    p: 0,
  },
  pdfCard: {
    p: 2,
    minHeight: '600px',
    flex: 1,
  },
  pdfIframe: {
    flex: 1,
    width: '100%',
    height: '400px',
    border: 'none',
    backgroundColor: 'white',
  },
  pdfErrorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    color: 'text.secondary',
  },
  contentSection: {
    mt: 4,
    mb: 2,
    overflow: 'auto',
  },
};

export default PayappProcess;
